import { ApolloQueryResult } from '@apollo/client';
import { SubscriberAddOnMethod } from 'types/electionService';
import apolloClient from 'api/apolloClient';
import {
  GET_CELTIC_USERS_FOR_SPONSOR_BY_TYPE,
  INVITE_TO_SELF_ENROLL,
  UPDATE_EMPLOYER,
  REMOVE_EMPLOYER,
  GET_EMPLOYERS_FOR_GROUP,
  USER_BY_TOKEN,
  EDIT_USER_INFORMATION,
  GET_USER_NPN,
  GET_AGENCY_ACCOUNT_REP,
  GET_SELF_REGISTRATION_LINK,
  SHARE_SELF_REGISTRATION_LINK,
  INVITE_EMPLOYEES_TO_GROUP,
  INVITE_SUBSCRIBER,
  REINVITE_SUBSCRIBER,
  GET_PRODUCER_LIST_BY_AGENCY,
  GET_USER_OR_PRODUCER_BY_ID,
} from 'api/queries/userService';
import { FetchResult } from 'apollo-link';
import { CelticType, FullUser, InputEmployer, Producer, SEUser, User, UserInfoInput } from 'types/userService';

export async function inviteToSelfEnroll(users: SEUser[]): Promise<FullUser[]> {
  return (
    await apolloClient.mutate<{ inviteToSelfEnroll: FullUser[] }>({
      mutation: INVITE_TO_SELF_ENROLL,
      variables: { users },
    })
  ).data!.inviteToSelfEnroll;
}

export async function shareSelfRegistrationLink(
  groupId: string,
  recipients: string[],
  message: string,
): Promise<FetchResult<{ shareSelfRegistrationLink: boolean }>> {
  return apolloClient.mutate({
    mutation: SHARE_SELF_REGISTRATION_LINK,
    variables: { groupId, recipients, message },
  });
}

export async function updateEmployer(user: InputEmployer): Promise<FullUser> {
  return (
    await apolloClient.mutate<{ updateEmployer: FullUser }>({
      mutation: UPDATE_EMPLOYER,
      variables: { user },
    })
  ).data!.updateEmployer;
}

export async function removeEmployer(groupId: string, userId: string): Promise<void> {
  await apolloClient.mutate({
    mutation: REMOVE_EMPLOYER,
    variables: { groupId, userId },
  });
}

export async function getEmployersForGroup(groupId: string): Promise<FullUser[]> {
  return (
    await apolloClient.query<{ getEmployersForGroup: FullUser[] }>({
      query: GET_EMPLOYERS_FOR_GROUP,
      variables: { groupId },
      fetchPolicy: 'no-cache',
    })
  ).data.getEmployersForGroup;
}

export async function getUserByToken(): Promise<FullUser> {
  return (
    await apolloClient.query<{ userByToken: FullUser }>({
      query: USER_BY_TOKEN,
      fetchPolicy: 'no-cache',
    })
  ).data.userByToken;
}

export async function editUserInformation(
  userInfo: UserInfoInput,
): Promise<FetchResult<{ editUserInformation: string }>> {
  return apolloClient.mutate({
    mutation: EDIT_USER_INFORMATION,
    fetchPolicy: 'no-cache',
    variables: { userInfo },
  });
}

export async function getUserNpn(): Promise<User> {
  const res = await apolloClient.query<{ userByToken: User }>({
    query: GET_USER_NPN,
    fetchPolicy: 'no-cache',
  });
  return res.data.userByToken;
}

export async function getAgencyAccountRep(agencyId?: string): Promise<FullUser> {
  const res = await apolloClient.query<{ getAgencyAccountRep: FullUser }>({
    query: GET_AGENCY_ACCOUNT_REP,
    fetchPolicy: 'no-cache',
    variables: { agencyId },
  });
  return res.data.getAgencyAccountRep;
}

export async function getCelticUsersForSponsorByType(
  type: CelticType,
  sponsorId?: string,
): Promise<ApolloQueryResult<{ getCelticUsersForSponsorByType: FullUser[] }>> {
  return apolloClient.query<{ getCelticUsersForSponsorByType: FullUser[] }>({
    query: GET_CELTIC_USERS_FOR_SPONSOR_BY_TYPE,
    variables: { type, sponsorId },
    fetchPolicy: 'no-cache',
  });
}

export async function getSelfRegistrationLink(groupId: string): Promise<string> {
  return (
    await apolloClient.query<{ getSelfRegistrationLink: string }>({
      query: GET_SELF_REGISTRATION_LINK,
      variables: { groupId },
      fetchPolicy: 'no-cache',
    })
  ).data.getSelfRegistrationLink;
}

export async function inviteEmployeesToGroup(
  groupId: string,
  employees: Record<string, string>[],
  inviteType: SubscriberAddOnMethod,
  encrypted: boolean = false,
  invite: boolean = false,
): Promise<boolean> {
  const result = await apolloClient.mutate<{
    inviteEmployeesToGroup: boolean;
  }>({
    mutation: INVITE_EMPLOYEES_TO_GROUP,
    variables: { groupId, employees, inviteType, encrypted, invite },
  });
  return result.data!.inviteEmployeesToGroup;
}

export async function inviteSubscriber(user: SEUser): Promise<FetchResult<boolean>> {
  return apolloClient.mutate({
    mutation: INVITE_SUBSCRIBER,
    variables: { user },
  });
}

export async function reinviteSubscriber(user: SEUser): Promise<FetchResult<boolean>> {
  return apolloClient.mutate({
    mutation: REINVITE_SUBSCRIBER,
    variables: { user },
  });
}

export async function getProducerListByAgency(id: string, state: string): Promise<Producer[]> {
  return (
    await apolloClient.query<{ getProducerListByAgency: Producer[] }>({
      query: GET_PRODUCER_LIST_BY_AGENCY,
      variables: { id, state },
      fetchPolicy: 'no-cache',
    })
  ).data.getProducerListByAgency;
}

export async function getUserOrProducerById(
  userId: string | null,
  producerId: string | null,
): Promise<FullUser | Producer> {
  return (
    await apolloClient.query<{ userOrProducerById: FullUser | Producer }>({
      query: GET_USER_OR_PRODUCER_BY_ID,
      variables: { userId, producerId },
      fetchPolicy: 'no-cache',
    })
  ).data.userOrProducerById;
}
